import { DefaultPrimaryButton, DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { Product } from '@frontend/product/utils';
import { FormattedMessage } from 'react-intl';

import { KioskNavigationFooter } from '../../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../../components/kiosk-navigation-header.component';
import { StockItemComponent } from '../../../components/stock-item.component';
import { SlotCard } from '../../components/slot.card.component';
import { useStockDetail } from '../../hooks/useStockDetail';

export const VendingStockDetail = () => {
    const viewProps = useStockDetail({ stockType: 'consumable' });

    if (viewProps.isLoading || !viewProps.product) {
        return (
            <div className='bg-background-default flex size-full flex-col items-center justify-start overflow-hidden'>
                <KioskNavigationHeader
                    title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                    onBack={() => viewProps.navigate(-1)}
                />
                <div className='bg-background-default inline-flex flex-col items-start justify-start gap-6 self-stretch p-6'>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div className='bg-background-default flex size-full flex-col items-center justify-start overflow-hidden'>
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={() => viewProps.navigate(-1)}
            />
            <div className='flex size-full flex-col items-start justify-start gap-6 self-stretch p-6'>
                <div className='flex w-full flex-col gap-3'>
                    <div className='text-foreground-default justify-center text-base font-medium'>
                        <FormattedMessage
                            defaultMessage='Selected product'
                            id='confirm-lending.selected-product'
                        />
                    </div>
                    <StockItemComponent
                        product={{
                            id: viewProps.product.id,
                            name: new Product(viewProps.product).getDefaultName(),
                            type: viewProps.product.type
                        }}
                        quantity={viewProps.totalQuantity}
                        isLast={false}
                        attributes={viewProps.product.attributes}
                    />
                </div>
                <div className='bg-background-surface border-border-default inline-flex flex-col items-end justify-center gap-2 self-stretch rounded-xl border p-4'>
                    <div className='inline-flex h-6 items-center justify-between self-stretch'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.PRODUCT.EAN}</div>
                        <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.product.ean}</div>
                    </div>
                    <div className='inline-flex h-6 items-center justify-between self-stretch'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.PRODUCT.SKU}</div>
                        <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.product.sku}</div>
                    </div>
                    <div className='bg-border-default h-px self-stretch' />
                    <div className='inline-flex h-6 items-center justify-between self-stretch'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.STOCK.CURRENT_STOCK}</div>
                        <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.totalQuantity}</div>
                    </div>
                    <div className='inline-flex h-6 items-center justify-between self-stretch'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.STOCK.MIN_STOCK}</div>
                        <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.requirement?.min ?? '/'}</div>
                    </div>
                    <div className='inline-flex h-6 items-center justify-between self-stretch'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.STOCK.MAX_STOCK}</div>
                        <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.requirement?.max ?? '/'}</div>
                    </div>
                </div>
                <div className='flex w-full flex-col gap-3'>
                    <div className='flex w-full justify-between'>
                        <div className='text-foreground-default justify-center text-base font-medium'>
                            <FormattedMessage
                                id='kiosk.manage-spot.stock-detail.stock-locations'
                                defaultMessage='Stock locations'
                            />
                        </div>
                        <button
                            type='button'
                            onClick={viewProps.selectAllLocations}
                            className='text-brand-primary text-base font-medium'
                            disabled={viewProps.stockLocations.length === 0}>
                            <FormattedMessage
                                id='select-all.message'
                                defaultMessage='Select all'
                            />
                        </button>
                    </div>
                    <div className='grid grid-cols-2 gap-2'>
                        {viewProps.stockLocations.map((location) => (
                            <SlotCard
                                key={location.id}
                                onClick={() => viewProps.toggleLocation(location.id)}
                                isSelected={viewProps.selectedLocationIds.has(location.id)}
                                slotName={location.slotName}
                                quantity={location.quantity}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <KioskNavigationFooter
                leftButton={
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.GO_BACK}
                        onClick={() => viewProps.navigate(-1)}
                    />
                }
                rightButton={
                    <DefaultPrimaryButton
                        text={
                            viewProps.selectedLocationCount < 2 ? (
                                <FormattedMessage
                                    id='kiosk.manage-spot.view-content-slot-detail.open-slot'
                                    defaultMessage='Open SLOT {name}'
                                    values={{
                                        name: viewProps.selectedLocations[0]?.slotName ?? ''
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id='kiosk.manage-spot.links.open-slots'
                                    defaultMessage='Open SLOTs'
                                />
                            )
                        }
                        disabled={viewProps.selectedLocationCount === 0 || viewProps.isOpeningSlots}
                        isLoading={viewProps.isOpeningSlots}
                        onClick={viewProps.openSelectedSlots}
                    />
                }
            />
        </div>
    );
};
